import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFetch, apiFetchEmpty } from './client';
import { keys } from './keys';
import { invalidateAllCommunityAudits } from './communityAudit';

const RowSchema = z.object({
  id: z.number().nullable(),
  payable_task_group: z.string(),
  phase_id: z.number().nullable(),
  phase_name: z.string().nullable(),
  sub_phase_id: z.number().nullable(),
  sub_phase_name: z.string().nullable(),
  supplier_category: z.string().nullable(),
  supplier_category_label: z.string().nullable(),
  phase_mapped: z.boolean(),
  supplier_mapped: z.boolean(),
});

export type PayableTaskGroupMappingRow = z.infer<typeof RowSchema>;

export type PayableTaskGroupPhasePayload = {
  payable_task_group: string;
  phase_id?: number | null;
  sub_phase_id?: number | null;
  supplier_category?: string | null;
};

function invalidateMappings(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: keys.payableTaskGroupPhases() });
  invalidateAllCommunityAudits(qc);
}

export function usePayableTaskGroupPhasesList() {
  return useQuery({
    queryKey: keys.payableTaskGroupPhases(),
    queryFn: () => apiFetch('/api/payable-task-group-phases', z.array(RowSchema)),
  });
}

export function useCreatePayableTaskGroupPhase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PayableTaskGroupPhasePayload) =>
      apiFetch('/api/payable-task-group-phases', z.object({ id: z.coerce.number() }), {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidateMappings(qc),
  });
}

export function useUpdatePayableTaskGroupPhase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: PayableTaskGroupPhasePayload }) =>
      apiFetchEmpty(`/api/payable-task-group-phases/${vars.id}`, {
        method: 'PUT',
        body: JSON.stringify(vars.body),
      }),
    onSuccess: () => invalidateMappings(qc),
  });
}

export function useUpsertPayableTaskGroupPhase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PayableTaskGroupPhasePayload) =>
      apiFetch('/api/payable-task-group-phases', RowSchema, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidateMappings(qc),
  });
}

export function useDeletePayableTaskGroupPhase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetchEmpty(`/api/payable-task-group-phases/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateMappings(qc),
  });
}
